import { memo, useState, useEffect } from 'react';
import { useVersionCheck } from '../hooks/useVersionCheck';

const bannerStyles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    padding: '12px 16px',
    textAlign: 'center',
    zIndex: 10000,
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap'
  },
  button: {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: '14px'
  }
};

const getBannerStyle = (updateType) => {
  const styles = {
    OPTIONAL: {
      backgroundColor: '#022b73',
      color: '#fff',
      button: {
        backgroundColor: '#fff',
        color: '#022b73'
      }
    },
    RECOMMENDED: {
      backgroundColor: '#f59e0b',
      color: '#fff',
      button: {
        backgroundColor: '#fff',
        color: '#f59e0b'
      }
    },
    REQUIRED: {
      backgroundColor: '#dc2626',
      color: '#fff',
      button: {
        backgroundColor: '#fff',
        color: '#dc2626'
      }
    },
    CRITICAL: {
      backgroundColor: '#991b1b',
      color: '#fff',
      button: {
        backgroundColor: '#fef2f2',
        color: '#991b1b'
      }
    }
  };
  return styles[updateType] || styles.OPTIONAL;
};

const UpdateBanner = () => {
  const { 
    updateAvailable, 
    updateRequired, 
    updateType, 
    timeWindow,
    latestVersion,
    features,
    forceUpdate,
    dismissUpdate,
    currentVersion
  } = useVersionCheck();

  const [showDetails, setShowDetails] = useState(false);

  // Auto-force update when required update is detected
  useEffect(() => {
    if (updateRequired && updateAvailable) {
      // Give user 30 seconds to see the banner, then force update
      const timer = setTimeout(() => {
        console.log('Auto-forcing mandatory update');
        forceUpdate();
      }, 30000); // 30 seconds

      return () => clearTimeout(timer);
    }
  }, [updateRequired, updateAvailable, forceUpdate]);

  if (!updateAvailable) return null;

  const style = getBannerStyle(updateType);

  const getMessage = () => {
    // All updates are mandatory
    return `Update required: Version ${latestVersion} must be installed immediately.`;
  };

  const canDismiss = false; // All updates are mandatory

  return (
    <div style={{ ...bannerStyles.container, ...style }}>
      <span style={{ fontWeight: 500 }}>
        {getMessage()}
      </span>
      
      <button 
        type="button" 
        onClick={() => setShowDetails(!showDetails)}
        style={{ ...bannerStyles.button, ...style.button, background: 'transparent', color: 'inherit' }}
      >
        {showDetails ? 'Hide Details' : 'View Details'}
      </button>

      {showDetails && (
        <div style={{ 
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'white',
          color: '#333',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          minWidth: '300px',
          zIndex: 10001,
          marginTop: '8px'
        }}>
          <div style={{ marginBottom: '12px' }}>
            <strong>Current:</strong> {currentVersion}<br />
            <strong>Latest:</strong> {latestVersion}
          </div>
          {features.length > 0 && (
            <div style={{ marginBottom: "12px" }}>
              <strong>What's new:</strong>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                {features.map((feature, index) => (
                  <li key={index}>{feature}</li>
                ))}
              </ul>
            </div>
          )}
          <div style={{ fontSize: '12px', color: '#666' }}>
            {updateType === 'CRITICAL' && '⚠️ This update contains critical security fixes.'}
            {updateType === 'REQUIRED' && '⚠️ This version will become mandatory soon.'}
            {updateType === 'RECOMMENDED' && 'ℹ️ Recommended for best experience.'}
            {updateType === 'OPTIONAL' && 'ℹ️ Optional update with improvements.'}
          </div>
        </div>
      )}

      <button 
        type="button" 
        onClick={forceUpdate}
        style={{ ...bannerStyles.button, ...style.button }}
      >
        {updateType === 'CRITICAL' ? 'Update Now (Required)' : 'Update Now'}
      </button>

      {canDismiss && (
        <button 
          type="button" 
          onClick={dismissUpdate}
          style={{ 
            ...bannerStyles.button, 
            background: 'transparent', 
            color: 'inherit',
            textDecoration: 'underline'
          }}
        >
          Remind Later
        </button>
      )}
    </div>
  );
};

export default memo(UpdateBanner);
